import { memo, useCallback, useMemo } from 'react';
import { mergeClasses } from '@fluentui/react-components';
import { Icon } from '@fluentui/react';
import { useProject, useIsProjectExpanded, useTasksByProject, useTotalHoursByProject, useDailyTotalsByProject } from '../state';
import { useProjectToggle } from '../hooks/useProjectToggle';
import { useInitialData } from '../../../hooks/useInitialData';
import { useTimeGridStyles } from '../useTimeGridStyles';
import { useDateColumnStyles } from '../hooks/useDateStyles';

type ProjectRowProps = {
    projectId: string;
    dates: string[];
    onToggle: (projectId: string) => void;
    style: React.CSSProperties;
};

export const ProjectRowComponent = memo(function ProjectRowComponent({ projectId, dates, onToggle, style }: ProjectRowProps) {
    // Fetch data via state hooks
    const project = useProject(projectId);
    const isExpanded = useIsProjectExpanded(projectId);
    const taskCount = useTasksByProject(projectId).length;
    const totalHours = useTotalHoursByProject(projectId);
    const dailyTotals = useDailyTotalsByProject(projectId);
    const { handleKeyDown } = useProjectToggle();
    const {
        data: {
            configuration: { workWeek, showAllocations },
        },
    } = useInitialData();
    const styles = useTimeGridStyles();
    const resolvedWorkWeek = useMemo(() => workWeek ?? [], [workWeek]);
    const dateColumnStyles = useDateColumnStyles(dates, resolvedWorkWeek);

    const handleClick = useCallback(() => {
        onToggle(projectId);
    }, [onToggle, projectId]);

    const handleKey = useCallback(
        (e: React.KeyboardEvent) => {
            handleKeyDown(e, projectId);
        },
        [handleKeyDown, projectId],
    );

    if (!project) return null;

    return (
        <div
            className={mergeClasses(styles.row, styles.rowProject, showAllocations && styles.rowWithAllocation)}
            style={style}
            role="row"
            aria-expanded={isExpanded}
            onClick={handleClick}
            onKeyDown={handleKey}
            tabIndex={0}
            aria-label={`${project.name}, ${taskCount} tasks, ${isExpanded ? 'expanded' : 'collapsed'}`}
        >
            <div className={mergeClasses(styles.cell, styles.cellProjectName, styles.stickyCol1)} role="gridcell">
                <Icon iconName="ChevronRight" className={mergeClasses(styles.expandIcon, isExpanded && styles.expandIconExpanded)} aria-hidden="true" />
                <span className={styles.ellipsisText} title={project.name}>
                    {project.name}
                </span>
                <span className={styles.taskCount}>({taskCount})</span>
            </div>

            <div className={mergeClasses(styles.cell, styles.stickyCol2)} role="gridcell">
                {/* Work type column - empty for project rows */}
            </div>

            {dates.map(date => (
                <div key={date} className={mergeClasses(styles.cell, styles.cellHours, dateColumnStyles[date])} role="gridcell">
                    {blankZero(dailyTotals[date]?.toString())}
                </div>
            ))}

            <div className={mergeClasses(styles.cell, styles.cellTotal)} role="gridcell">
                {blankZero(totalHours.toString())}
            </div>

            {showAllocations && (
                <div className={mergeClasses(styles.cell, styles.cellAllocation)} role="gridcell">
                    {blankZero(project.allocation?.toString())}
                </div>
            )}
        </div>
    );
});

const blankZero = (val: string) => (val === '0' || !val ? '' : val);
